import React, { useState, useEffect } from 'react';
import App from '../../App';
import { Route } from 'react-router-dom';
import {
    Button,
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { getManageAccesses, putManageAccesses } from "../API/manageAccessAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";
// Function to extract routes from children
function extractPaths(children) {
    let paths = [];

    React.Children.forEach(children, (child) => {
        if (child.type === Route && child.props.path) {
            paths.push(child.props.path);
        }
        if (child.props && child.props.children) {
            paths = paths.concat(extractPaths(child.props.children));
        }
    });

    return paths;
}

// Paths to exclude
const excludedPaths = [
    '/login',
    '/logout',
    '/not-found',
    '*',
    '/list-lead-schedules',
    '/test-scheduler',
    '/plot-schedule',
    '/job-schedules',
    '/testing-job-orders',
    '/',
    '/not-authorized',
    '/reset-password'
];

// Helper to format paths to labels and remove /:id
function formatPathToLabel(path) {
    const cleanPath = path.replace(/\/:[^/]+$/, '');  // Remove trailing /:id
    return cleanPath
        .replace(/^\//, '')  // Remove leading slash
        .replace(/-/g, ' ')  // Replace hyphens with spaces
        .split(' ')          // Split words by space
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))  // Capitalize first letter
        .join(' ');          // Join words back together
}

// Function to categorize paths into sections
function categorizePaths(paths) {
    const sections = {
        Operations: [],
        Sales: [],
        "Client Retention": [],
        Payroll: [],
        "Admin Hub": [],
        Other: []
    };

    const operationsPaths = [
        'add-technician',
        'assign-technicians',
        'batch-assign',
        'create-accomplishment-reports',
        'create-endorsement-form',
        'create-job-order',
        'create-service-report',
        'delete-job-order',
        'delete-makati-gpc-form',
        'delete-qc-form',
        'delete-termite-control-form',
        'delete-transmittal-form',
        'edit-job-order',
        'edit-makati-gpc-form',
        'edit-qc-form',
        'edit-service-report',
        'edit-technician',
        'edit-transmittal-form',
        'edit-endorsement-form',
        'manage-endorsement-form',
        'manage-job-orders',
        'manage-service-reports',
        'manage-technicians',
        'makati-gpc-form',
        'qc-form',
        'submit-endorsement-form',
        'submit-service-report',
        'termite-control-form',
        'transmittal-form',
        'view-calendar',
        'view-competency-skills-matrix',
        'view-daily-forecast',
        'view-endorsement-form',
        'view-job-order',
        'view-makati-gpc-form',
        'view-open-job-orders-report',
        'view-operation-completion-report',
        'view-productivity-report',
        'view-professional-skills-matrix',
        'view-qc-form',
        'view-rest-day',
        'view-service-report',
        'view-technician',
        'view-termite-control-form',
        'view-transmittal-form'
    ];

    const salesPaths = [
        'add-new-lead',
        'create-salesrep',
        'edit-lead',
        'manage-leads',
        'manage-salesreps',
        'view-lead',
        'view-lead-status-report',
        'view-salesrep'
    ];

    const clientRetentionPaths = [
        'create-client',
        'job-order-history',
        'list-email-exceptions',
        'manage-clients',
        'manage-email-exception',
        'send-job-order-reminder',
        'send-promotions',
        'view-client-service-reports'
    ];

    const payrollPaths = [
        'payroll-final-calculation',
        'payroll-reports',        
        'technician-hours',
        'technician-compensation'
    ];

    const adminHubPaths = [
        'change-password',
        'create-user',
        'delete-technicians',
        'delete-users',
        'manage-access',
        'manage-users'
    ];

    paths.forEach(path => {
        const pathWithoutSlash = path.replace(/^\//, '');
        const pathKey = pathWithoutSlash.replace(/\/:[^/]*$/, ''); // Remove /:id params
        
        if (operationsPaths.some(op => pathKey.includes(op))) {
            sections.Operations.push(path);
        } else if (salesPaths.some(sp => pathKey.includes(sp))) {
            sections.Sales.push(path);
        } else if (clientRetentionPaths.some(cr => pathKey.includes(cr))) {
            sections["Client Retention"].push(path);
        } else if (payrollPaths.some(pr => pathKey.includes(pr))) {
            sections.Payroll.push(path);
        } else if (adminHubPaths.some(ah => pathKey.includes(ah))) {
            sections["Admin Hub"].push(path);
        } else {
            sections.Other.push(path);
        }
    });

    // Remove empty sections
    Object.keys(sections).forEach(key => {
        if (sections[key].length === 0) {
            delete sections[key];
        }
    });

    return sections;
}

function ManageAccess() {
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const appRoutes = App().props.children.props.children;
    const routePaths = extractPaths(appRoutes);

    // Filter out excluded and empty paths
    const filteredPaths = routePaths
        .filter(path => path && !excludedPaths.includes(path))
        .sort((a, b) => a.localeCompare(b));
    
    const categorizedPaths = categorizePaths(filteredPaths);
    const [rolesData, setRolesData] = useState([]);
    const [roleRoutes, setRoleRoutes] = useState([]);

    const theme = createTheme({
        palette: {
            primary: {
                main: "#1e5d45", // Custom primary color
            },
        },
    });

    const StyledHr = styled("hr")(({ theme }) => ({
        border: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: theme.spacing(4, 0),
        opacity: 0.8,
    }));

    // Add this inside the component
    const [selectedRole, setSelectedRole] = useState('');
    useEffect(() => {
        async function fetchRoles() {
            const result = await getManageAccesses();
            if (result) {
                setRolesData(result);
            }
        }
        fetchRoles();
    }, []);


    const handleRoleChange = (event) => {
        setSelectedRole(event.target.value);
    };

    // Define this above the component
    const roles = [
        "sales",
        "operations_admin",
        "operations_coordinator",
        "operations_technician",
        "finance",
        "account_manager",
        "office_admin",
        "customer_service",
        "system_admin"
    ];

        const handleSubmit = async () => {
        const selectedRoleData = rolesData.find(r => r.role === selectedRole);

        if (!selectedRoleData) {
            setErrorMessage('Please select a role before submitting.');
            return;
        }

        // Check if the selected role is system_admin
        const shouldIncludeWildcard = selectedRole === 'system_admin';

        // Filter out wildcard (*) if the role is not system_admin
        const filteredExcludedPaths = shouldIncludeWildcard
            ? excludedPaths
            : excludedPaths.filter(path => path !== '*');

        // Merge routes and filter wildcard (*) if necessary
        let updatedRoutes = [...new Set([...selectedRoleData.routes, ...filteredExcludedPaths])];

        if (!shouldIncludeWildcard) {
            updatedRoutes = updatedRoutes.filter(route => route !== '*');
        }

        // Call API to update access
        try {
            await putManageAccesses(selectedRoleData.id, { routes: updatedRoutes });
            setSuccessMessage('Access updated successfully.');
            setErrorMessage('');
        } catch (error) {
            setErrorMessage('Failed to update access.');
            setSuccessMessage('');
        }
    };





    return (

        <BaseWrapper
            titleIcon={<VpnKeyIcon />}
            title="Admin Hub"
            subTitle="Manage Access"
            subTitleRoute={"/manage-access"}
            // createButtonLabel="Create User"
            // onClickCreateButton={() => navigate("/create-user")}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <FormControl fullWidth sx={{ marginBottom: 3 }}>
                <InputLabel 
                    id="role-select-label" 
                    sx={{ 
                        fontSize: '0.875rem',
                        '&.MuiInputLabel-shrunk': {
                            transform: 'translate(14px, -9px) scale(0.75)'
                        }
                    }}
                >
                    Select Role
                </InputLabel>
                <Select
                    labelId="role-select-label"
                    value={selectedRole}
                    onChange={(e) => {
                        const selected = e.target.value;
                        setSelectedRole(selected);
                    }}
                    label="Select Role"
                    sx={{ 
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '16.5px',
                            paddingBottom: '16.5px'
                        }
                    }}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {roles.map((role) => {
                        const roleData = rolesData.find(r => r.role === role);
                        return (
                            <MenuItem key={role} value={role}>
                                {role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {/* Checkbox Group Section */}
            <Box sx={{
                maxHeight: '500px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: 2,
                backgroundColor: '#f9f9f9'
            }}>
                <Typography variant="subtitle1" gutterBottom>
                    Manage Access Permissions
                </Typography>
                
                {Object.entries(categorizedPaths).map(([sectionName, paths]) => {
                    const selectedRoleData = rolesData.find(r => r.role === selectedRole);
                    const checkedPathsInSection = selectedRoleData ? paths.filter(path => selectedRoleData.routes.includes(path)) : [];
                    const isAllChecked = paths.length > 0 && checkedPathsInSection.length === paths.length;
                    const isPartiallyChecked = checkedPathsInSection.length > 0 && checkedPathsInSection.length < paths.length;

                    const handleSelectAllSection = () => {
                        setRolesData((prevRoles) =>
                            prevRoles.map((role) => {
                                if (role.role === selectedRole) {
                                    let updatedRoutes;
                                    if (isAllChecked) {
                                        // Uncheck all in this section
                                        updatedRoutes = role.routes.filter(route => !paths.includes(route));
                                    } else {
                                        // Check all in this section
                                        const newPaths = paths.filter(path => !role.routes.includes(path));
                                        updatedRoutes = [...role.routes, ...newPaths];
                                    }
                                    return { ...role, routes: updatedRoutes };
                                }
                                return role;
                            })
                        );
                    };

                    return (
                        <Box key={sectionName} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        color: '#1e5d45', 
                                        fontWeight: 'bold',
                                        borderBottom: '2px solid #1e5d45',
                                        paddingBottom: 1,
                                        flexGrow: 1,
                                        marginRight: 2
                                    }}
                                >
                                    {sectionName}
                                </Typography>
                                <Button
                                    size="small"
                                    variant={isAllChecked ? "contained" : "outlined"}
                                    color={isPartiallyChecked ? "warning" : "primary"}
                                    onClick={handleSelectAllSection}
                                    disabled={!selectedRole}
                                    sx={{
                                        minWidth: '120px',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        borderRadius: '20px'
                                    }}
                                >
                                    {isAllChecked ? 'Unselect All' : isPartiallyChecked ? 'Select All' : 'Select All'}
                                </Button>
                            </Box>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {paths.map((path, index) => {
                                    const selectedRoleData = rolesData.find(r => r.role === selectedRole);
                                    const isChecked = selectedRoleData ? selectedRoleData.routes.includes(path) : false;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={`${sectionName}-${index}`}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        value={path}
                                                        checked={isChecked}
                                                        onChange={() => {
                                                            setRolesData((prevRoles) =>
                                                                prevRoles.map((role) => {
                                                                    if (role.role === selectedRole) {
                                                                        const updatedRoutes = isChecked
                                                                            ? role.routes.filter(r => r !== path)
                                                                            : [...role.routes, path];
                                                                        return { ...role, routes: updatedRoutes };
                                                                    }
                                                                    return role;
                                                                })
                                                            );
                                                        }}
                                                    />
                                                }
                                                label={formatPathToLabel(path)}
                                                sx={{ 
                                                    whiteSpace: 'nowrap',
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </FormGroup>
                    </Box>
                );})}
            </Box>

            <Button
                variant="contained"
                color="primary"
                sx={{ marginTop: 3 }}
                onClick={handleSubmit}
            >
                Submit
            </Button>
            
        </BaseWrapper>
        // <ThemeProvider theme={theme}>
        //     <Box sx={{ padding: "40px 60px" }}>
        //         <Card
        //             variant="outlined"
        //             style={{
        //                 marginTop: "40px",
        //                 marginBottom: "40px",
        //                 borderRadius: 0,
        //             }}
        //         >
        //             <CardHeader
        //                 title={
        //                     <Box
        //                         display="flex"
        //                         alignItems="center"
        //                         justifyContent="space-between"
        //                     >
        //                         <Box display="flex" alignItems="center">
        //                             <VpnKeyIcon
        //                                 sx={{
        //                                     fontSize: "40px",
        //                                     marginRight: "8px",
        //                                 }}
        //                             />
        //                             <Typography variant="h6">Admin Hub</Typography>
        //                         </Box>
        //                         <Breadcrumbs
        //                             aria-label="breadcrumb"
        //                             separator={<NavigateNextIcon fontSize="small" />}
        //                             sx={{ color: "white" }}
        //                         >
        //                             <Typography sx={{ color: "#ffffff99" }}>
        //                                 Admin Hub
        //                             </Typography>
        //                             <Typography
        //                                 component={Link}
        //                                 to="/manage-technicians"
        //                                 sx={{ color: "#ffffff", textDecoration: "none" }}
        //                             >
        //                                 Manage Access
        //                             </Typography>
        //                         </Breadcrumbs>
        //                     </Box>
        //                 }
        //                 sx={{
        //                     backgroundColor: "#1e5d45",
        //                     color: "white",
        //                     padding: "16px",
        //                 }}
        //             />
        //             <CardContent sx={{ padding: '20px' }}>
        //                 <Card variant="outlined" sx={{ padding: '20px' }}>
        //                     <CardHeader
        //                         title={
        //                             <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
        //                                 Manage Access
        //                             </Typography>
        //                         }
        //                         sx={{ marginBottom: 2 }}
        //                     />
        //                     <CardContent>
        //                         {/* Role Selection */}
        //                         <FormControl fullWidth sx={{ marginBottom: 3 }}>
        //                             <InputLabel id="role-select-label" sx={{ fontSize: '0.875rem' }}>Select Role</InputLabel>
        //                             <Select
        //                                 labelId="role-select-label"
        //                                 value={selectedRole}
        //                                 onChange={(e) => {
        //                                     const selected = e.target.value;
        //                                     setSelectedRole(selected);
        //                                 }}
        //                                 label="Select Role"
        //                                 sx={{ fontSize: '0.875rem' }}
        //                             >
        //                                 <MenuItem value="">
        //                                     <em>None</em>
        //                                 </MenuItem>
        //                                 {roles.map((role) => {
        //                                     const roleData = rolesData.find(r => r.role === role);
        //                                     return (
        //                                         <MenuItem key={role} value={role}>
        //                                             {role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
        //                                         </MenuItem>
        //                                     );
        //                                 })}
        //                             </Select>
        //                         </FormControl>

        //                         {/* Checkbox Group Section */}
        //                         <Box sx={{
        //                             maxHeight: '300px',
        //                             overflowY: 'auto',
        //                             border: '1px solid #ddd',
        //                             borderRadius: '4px',
        //                             padding: 2,
        //                             backgroundColor: '#f9f9f9'
        //                         }}>
        //                             <Typography variant="subtitle1" gutterBottom>
        //                                 Manage Access Permissions
        //                             </Typography>
        //                             <FormGroup>
        //                                 <Grid container spacing={1}>
        //                                     {(() => {
        //                                         const totalColumns = 3;
        //                                         const sortedPaths = [...filteredPaths].sort((a, b) => a.localeCompare(b));
        //                                         const rowsPerColumn = Math.ceil(sortedPaths.length / totalColumns);

        //                                         // Create vertical division of paths (one-third for each column)
        //                                         const columnOrder = Array.from({ length: rowsPerColumn }, (_, rowIndex) =>
        //                                             sortedPaths.filter((_, i) => i % rowsPerColumn === rowIndex)
        //                                         ).flat();

        //                                         // Render paths top to bottom in each column
        //                                         return columnOrder.map((path, index) => {
        //                                             const selectedRoleData = rolesData.find(r => r.role === selectedRole);
        //                                             const isChecked = selectedRoleData ? selectedRoleData.routes.includes(path) : false;

        //                                             return (
        //                                                 <Grid item xs={12} sm={4} key={index}>
        //                                                     <FormControlLabel
        //                                                         control={
        //                                                             <Checkbox
        //                                                                 value={path}
        //                                                                 checked={isChecked}
        //                                                                 onChange={() => {
        //                                                                     setRolesData((prevRoles) =>
        //                                                                         prevRoles.map((role) => {
        //                                                                             if (role.role === selectedRole) {
        //                                                                                 const updatedRoutes = isChecked
        //                                                                                     ? role.routes.filter(r => r !== path)
        //                                                                                     : [...role.routes, path];
        //                                                                                 return { ...role, routes: updatedRoutes };
        //                                                                             }
        //                                                                             return role;
        //                                                                         })
        //                                                                     );
        //                                                                 }}
        //                                                             />
        //                                                         }
        //                                                         label={formatPathToLabel(path)}
        //                                                         sx={{ whiteSpace: 'nowrap' }}
        //                                                     />
        //                                                 </Grid>
        //                                             );
        //                                         });
        //                                     })()}
        //                                 </Grid>
        //                             </FormGroup>




        //                         </Box>

        //                         <Button
        //                             variant="contained"
        //                             color="primary"
        //                             sx={{ marginTop: 3 }}
        //                             onClick={handleSubmit}
        //                         >
        //                             Submit
        //                         </Button>

        //                     </CardContent>

        //                 </Card>
        //             </CardContent>
        //         </Card>
        //     </Box>
        // </ThemeProvider>
    );
}

export default ManageAccess;
